import {Injectable} from '@angular/core';
import {ApplicationStateService} from './application-state.service';
import {SessionStorage} from '../../core/services/global/storage/session-storage.service';
import {PaymentAcceptStatus} from '../../core/services/impl/payment.service';
import {PaymentAuthorizationStatus} from '../sections/notifications/insurer-data-authorization-notification/insurer-data-authorization.component';
import {MetricsService} from './metrics.service';
import {CustomMetricsName} from '../components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../components/models/metrics/metrics-type.enum';
import {AppMode} from '../models/app-mode.enum';
import {
  NextNotificationButtonAction,
  NextNotificationDataConfig,
  NextNotificationState,
  NextSuccessNotificationContext,
  NotificationState
} from '../models/notification-state';
import {isNext} from '../../core/utils/utils';
import {NotificationComponentKey} from '../../core/enums/components-keys';

@Injectable({
  providedIn: 'root'
})
export class NotificationStateService {

  notificationMode: NotificationMode;
  awaiting = true;
  isMessageTranslated: boolean;
  loaderVisible = true;
  destroyed = false;

  notificationState: NotificationState;
  nextNotificationState: NextNotificationState;
  isNext = isNext();

  nextNotificationDataConfig: NextNotificationDataConfig[] = [
    {
      context: NextSuccessNotificationContext.DIAGNOSTIC_STATION,
      state: {
        title: 'NOTIFICATION.LEAD.title',
        image: './assets/images/icons/next-notification-success.svg',
        buttons: [
          {
            label: 'NOTIFICATION.LEAD.buttonLabel',
            action: NextNotificationButtonAction.RESET_APP,
            cssClass: 'btn--secondary'
          }
        ]
      }
    },
    {
      context: NextSuccessNotificationContext.PAYMENT_SUCCESS,
      state: {
        title: 'NOTIFICATION.PAYMENT_SUCCESS.title',
        subtitle: 'NOTIFICATION.PAYMENT_SUCCESS.subtitle',
        image: './assets/images/icons/next-notification-success.svg',
        buttons: [
          {
            label: 'NOTIFICATION.PAYMENT_SUCCESS.calculateAssistanceButtonLabel',
            action: NextNotificationButtonAction.REDIRECT,
            redirectUrl: 'https://kioskpolis.pl/ubezpieczenie-assistance/',
            cssClass: 'btn--secondary'
          },
        ]
      }
    }
  ];

  private readonly NO_POLICY_INFO_TRANSLATION_KEY = 'noPolicyInfo';

  constructor(private metricsService: MetricsService,
              private applicationStateService: ApplicationStateService,
              private sessionStorage: SessionStorage) {
  }

  get canInitPaymentStatusFromSession(): boolean {
    return !this.destroyed && this.awaiting;
  }

  handleNoPolicyInfo(): void {
    this.loaderVisible = false;
    this.setMessage(this.NO_POLICY_INFO_TRANSLATION_KEY);
  }

  setAppMode(mode: AppMode): void {
    this.applicationStateService.mode = mode;
  }

  setMessage(message: string) {
    if (this.awaiting) {
      this.setNotificationState({
        message: message
      });
      this.isMessageTranslated = message === this.NO_POLICY_INFO_TRANSLATION_KEY;
    }
  }

  buildNotificationForResolvedPaymentStatus(paymentStatus): void {
    this.awaiting = false;
    const componentKey = this.getNotificationKey(paymentStatus.code);
    this.setNotification(componentKey, paymentStatus);
  }

  buildWebSocketPaymentNotification(notification): void {
    this.awaiting = false;
    if (notification.body.status.status === 409) {
      this.setPaymentAcceptNotification(notification.body.status);
    } else if (notification.body.status === 406) {
      this.setAuthorizationInsurerDataNotification(notification.body);
    } else if (notification.body.status === 407) {
      this.setRegistrationFailedNotification(notification.body);
    } else {
      const status = notification.body.status.hasOwnProperty('status') ? notification.body.status : notification.body;
      const componentKey = this.getNotificationKey(status.status);
      this.setNotification(componentKey, status);
    }
  }

  setPaymentAcceptNotification(status: PaymentAcceptStatus): void {
    this.awaiting = false;
    this.setNotificationState({
      activeNotification: NotificationComponentKey.PAYMENT_ACCEPT,
      paymentAcceptStatus: status
    });
  }

  setNextSuccessNotification(context: NextSuccessNotificationContext): void {
    this.awaiting = false;
    this.setNotificationState({
      activeNotification: NotificationComponentKey.NEXT_SUCCESS,
    });
    const nextNotificationConfig = this.nextNotificationDataConfig.find(config => config.context === context);
    this.nextNotificationState = nextNotificationConfig ? nextNotificationConfig.state : null;
  }

  setAuthorizationInsurerDataNotification(status: PaymentAuthorizationStatus): void {
    this.awaiting = false;
    this.setNotificationState({
      activeNotification: NotificationComponentKey.PAYMENT_ACCEPT,
      paymentAuthorizationStatus: status
    });
  }

  setRegistrationFailedNotification(status): void {
    this.awaiting = false;
    this.setNotificationState({
      activeNotification: NotificationComponentKey.REGISTRATION_FAILED,
      registrationFailedStatus: status
    });
  }

  setNotificationMode(mode: NotificationMode) {
    this.notificationMode = mode;
  }

  sendNotificationComponentMetric(componentName: string, data: any): void {
    const value: NotificationComponentMetricValue = {
      component: componentName,
      data: data
    };
    this.metricsService.sendSimpleMetric(CustomMetricsName.NOTIFICATION_COMPONENT, MetricsType.COMPONENT, JSON.stringify(value));
  }

  private setNotification(notificationKey: NotificationComponentKey, status: any) {
    switch (notificationKey) {
      case NotificationComponentKey.PAYMENT_SUCCESS:
        this.setNotificationState({
          activeNotification: NotificationComponentKey.PAYMENT_SUCCESS,
          message: status.message,
          paymentMethod: status.hasOwnProperty('paymentMethod') ? status.paymentMethod : null,
          status: status.status
        });
        break;
      case NotificationComponentKey.NEXT_SUCCESS:
        this.setNextSuccessNotification(NextSuccessNotificationContext.PAYMENT_SUCCESS);
        break;
      default:
        this.setNotificationState({
          activeNotification: NotificationComponentKey.PAYMENT_ERROR,
          message: status.message,
        });
    }
  }

  private getNotificationKey(status: number): NotificationComponentKey {
    let componentKey: NotificationComponentKey;
    if (this.isNext) {
      // success notification for 402 error should be displayed only after payment
      if (status === 200 || (status === 402 && this.applicationStateService.redirectedToPayment)) {
        componentKey = NotificationComponentKey.NEXT_SUCCESS;
        this.clearStorage();
      } else {
        componentKey = NotificationComponentKey.PAYMENT_ERROR;
      }
    } else {
      if (status === 200) {
        componentKey = NotificationComponentKey.PAYMENT_SUCCESS;
        this.clearStorage();
      } else {
        componentKey = NotificationComponentKey.PAYMENT_ERROR;
      }
    }

    return componentKey;
  }

  private clearStorage(): void {
    this.sessionStorage.clear();
  }

  private setNotificationState(state: NotificationState): void {
    this.notificationState = state;
  }
}

export enum NotificationMode {
  PAYMENT_STATUS = 'paymentStatus'
}

export interface NotificationComponentMetricValue {
  component: string; // notification component name
  data: any; // object containing component dynamic data (e.g. messages extracted from notification)
}
